import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  useTheme,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ContactForm = ({ onSuccess }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    enquiry: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.email.trim() || !formData.enquiry.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setFormData({ email: '', enquiry: '' });
        if (onSuccess) {
          onSuccess(data);
        }
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(data.error || 'Failed to submit enquiry');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card 
        elevation={0}
        sx={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 4,
          maxWidth: 600,
          width: '100%',
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Alert 
            severity="success" 
            sx={{ 
              fontSize: '1rem',
              '& .MuiAlert-message': {
                fontSize: 'inherit'
              }
            }}
          >
            <Typography variant="h6" gutterBottom>
              Thank you for your enquiry!
            </Typography>
            <Typography variant="body2">
              We've received your message and will get back to you soon.
            </Typography>
          </Alert>
          <Button
            onClick={() => setSuccess(false)}
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
          >
            Send another enquiry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      elevation={0}
      sx={{
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        maxWidth: 600,
        width: '100%',
        transition: 'transform 200ms ease, box-shadow 200ms ease',
        '&:hover': { 
          transform: 'translateY(-2px)', 
          boxShadow: '0 10px 24px rgba(0,0,0,0.08)' 
        },
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontSize: { xs: '1.25rem', md: '1.5rem' },
            fontWeight: 600,
            mb: 2 
          }}
        >
          Get in touch
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '0.875rem', md: '1rem' }
          }}
        >
          Have a question about our services? We'd love to hear from you.
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              fontSize: '0.875rem'
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              name="email"
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              name="enquiry"
              label="Your Enquiry"
              value={formData.enquiry}
              onChange={handleChange}
              required
              fullWidth
              multiline
              rows={4}
              disabled={loading}
              placeholder="Tell us about your project or ask us a question..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <MessageIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                borderRadius: 2,
                py: 1.5,
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 600,
                background: 'linear-gradient(135deg, #0087c1 0%, #0066a1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0066a1 0%, #004d81 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 24px rgba(0, 135, 193, 0.3)',
                },
                '&:disabled': {
                  background: 'action.disabledBackground',
                },
                transition: 'all 0.3s ease',
              }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Sending...' : 'Send Enquiry'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
